import { Actor, CollisionType, Color, Engine, GpuParticleEmitter, vec, Vector } from 'excalibur';
import { getUnitAnimations, smokeAnimation } from './animations';
import { isInsideElipsis, randomGaussianValue } from '@game-libs/exc-libs/src/helpers/math.helper';
import { EventsManager, Unsubscribe } from '@game-libs/events-manager';
import { GameplayEvents } from '../types/game-events';
import { GUIActions } from '../types/game-types';
import { useGameStatusStore } from '../store/zustand/useGameStatusStore';
import { Images } from './resources';
import { arrowParticle, magicMissileParticle } from '@game-libs/exc-libs/src/particles/bolt';
import { boulderExplosionParticle } from '@game-libs/exc-libs';
import { GlobalGameStore } from '@game-libs/global-game-store';
import { BaseLevel } from './baseLevel';
import type { GameLevelId } from '../types/game-levels';

const positioningRules = {
  warrior: {
    center: 100,
    spread: 300,
    skew: 3,
  },
  wizard: {
    center: 250,
    spread: 700,
    skew: 0,
  },
  thief: {
    center: 180,
    spread: 500,
    skew: -10,
  },
  elf: {
    center: 450,
    spread: 1200,
    skew: -20,
  },
  bard: {
    center: 300,
    spread: 1200,
    skew: 0,
  },
  cleric: {
    center: 300,
    spread: 800,
    skew: 0,
  },
  catapult: {
    center: 500,
    spread: 500,
    skew: 0,
  },
};

type UnitType = 'warrior' | 'wizard' | 'elf' | 'thief' | 'cleric' | 'bard' | 'catapult';

const projectileConfig: Partial<Record<UnitType, { particle: any; speed: number }>> = {
  wizard: {
    particle: magicMissileParticle,
    speed: 200,
  },
  elf: {
    particle: arrowParticle,
    speed: 500,
  },
} as const;

// Global projectile limiter to reduce GC pressure (especially in Firefox)
const MAX_ACTIVE_PROJECTILES = 15;
let activeProjectileCount = 0;

function adjustToPerspective(x: number, y: number) {
  return x + (x * y) / 800;
}

function adjustAroundCenter(x: number, y: number) {
  const atan = Math.atan((y * 2) / x);

  return x * Math.cos(atan);
}

export class Unit extends Actor {
  unitType: UnitType;
  countOfType: number;
  isStunned = false;
  isIdle = false;
  boltInterval: NodeJS.Timeout | null = null;
  private subscriptions: Unsubscribe[] = [];

  constructor(unitType: UnitType, countOfType: number) {
    super();

    const { on, emit } = EventsManager();
    const { state: globalState } = GlobalGameStore();

    this.unitType = unitType;
    this.countOfType = countOfType;

    const animations = getUnitAnimations(unitType);

    const resumeAttackAnimation = () => {
      animations.attack.reset();
      this.graphics.use(animations.attack);
      this.isIdle = false;
    };

    const currentAnimation = animations.attack;
    currentAnimation.speed = Math.random() * 0.5 - 0.25 + currentAnimation.speed;

    this.graphics.use(currentAnimation);

    currentAnimation.events.on('frame', (frameEvent) => {
      if (!this.scene) {
        return;
      }

      if (unitType === 'catapult') {
        const engine = this.scene.engine;

        if (frameEvent.frameIndex === 3) {
          emit(GameplayEvents.SHOOT_CATAPULT);

          const shootsCats = globalState.globalVariables.value.shootsCats;

          const boltColor = shootsCats ? Color.Orange : Color.Gray;

          const bolt = new Actor({
            x: this.pos.x,
            y: this.pos.y - 20,
            width: 7,
            height: 7,
            color: boltColor,
            collisionType: CollisionType.PreventCollision,
          });

          engine.add(bolt);
          bolt.z = 2000;

          const offsetPosition = (this.scene as BaseLevel).getOffsetPosition();
          const boltTarget = vec(
            offsetPosition.x + Math.random() * 70 - 35,
            offsetPosition.y - 50 + Math.random() * 50 - 25,
          );

          // bolt.actions.moveTo(boltTarget, speed).die();
          const middlePoint1 = this.pos.x + (boltTarget.x - this.pos.x) * 0.33;
          const middlePoint2 = this.pos.x + (boltTarget.x - this.pos.x) * 0.66;
          const controlPoints = [
            vec(middlePoint1, this.pos.y - 400),
            vec(middlePoint2, this.pos.y - 400),
            boltTarget,
          ] as unknown as [control1: Vector, control2: Vector, end: Vector];
          const debris = new GpuParticleEmitter(boulderExplosionParticle({ pos: boltTarget, color: boltColor }));
          bolt.actions
            .curveTo({ controlPoints, duration: 1000, mode: 'dynamic' })
            .callMethod(() => {
              engine.add(debris);
              debris.z = 2000;
              bolt.graphics.opacity = 0;
            })
            .delay(150)
            .callMethod(() => {
              debris.isEmitting = false;
              debris.kill();
            })
            .die();
        }
      }
    });

    if (unitType !== 'catapult') {
      this.scale = vec(3, 3);
    } else {
      this.scale = vec(1.5, 1.5);
    }

    this.subscriptions.push(
      on(GameplayEvents.WAIT_FOR_LAST_HIT, () => {
        this.graphics.use(animations.idle);
        this.isIdle = true;
        this.boltInterval && clearInterval(this.boltInterval);
      }),
    );

    this.subscriptions.push(
      on(GameplayEvents.STUN_UNITS, () => {
        this.isStunned = true;
        this.graphics.use(animations.stunned);
      }),
    );

    this.subscriptions.push(
      on(GameplayEvents.UNSTUN_UNITS, () => {
        this.isStunned = false;
        resumeAttackAnimation();
      }),
    );

    this.subscriptions.push(
      on(GUIActions.WIN_GAME, () => {
        this.graphics.use(animations.idle);
        this.isIdle = true;
      }),
    );

    this.subscriptions.push(
      on(GUIActions.DUMMY_KILLED, () => {
        this.graphics.use(animations.idle);
        this.isIdle = true;
        this.boltInterval && clearInterval(this.boltInterval);
      }),
    );

    if (unitType === 'wizard') {
      this.subscriptions.push(
        on(GUIActions.NOT_ENOUGH_MANA, () => {
          this.graphics.use(animations.idle);
          this.isIdle = true;
        }),
      );

      this.subscriptions.push(
        on(GUIActions.ENOUGH_MANA, () => {
          resumeAttackAnimation();
        }),
      );
    }

    // Tutorial victory sets `won`; endless/dummy should keep attack loops running.
    const gameStatus = useGameStatusStore.getState();
    const currentLevelName = GlobalGameStore().state.currentLevel.value as GameLevelId | '';
    const isEndlessDummyLevel =
      gameStatus.dragonName === 'dummy' || currentLevelName === 'dummy';
    if (gameStatus.won && !isEndlessDummyLevel) {
      this.graphics.use(animations.idle);
      this.isIdle = true;
    }
  }

  onPostUpdate(engine: Engine, elapsed: number): void {
    if (this.graphics.current) {
      // this.graphics.current.tint = Color.fromHex('#D0D0D0');
    }
  }

  onRemove(): void {
    // Clean up interval
    if (this.boltInterval) {
      clearInterval(this.boltInterval);
      this.boltInterval = null;
    }

    // Clean up all event subscriptions
    this.subscriptions.forEach((unsub) => unsub());
    this.subscriptions = [];
  }

  onAdd(engine: Engine): void {
    if (projectileConfig[this.unitType]) {
      // Scale interval based on unit count - more units = longer intervals per unit
      // This keeps total projectile rate roughly constant regardless of unit count
      const scaleFactor = Math.max(1, this.countOfType / 5);
      const baseDelay = (Math.random() * 5000 + 2000) * scaleFactor;

      const fireProjectile = () => {
        if (this.isStunned || this.isIdle || document.hidden || !this.scene) return;

        // Global limit to reduce GC pressure from particle emitters
        if (activeProjectileCount >= MAX_ACTIVE_PROJECTILES) return;

        const projectile = projectileConfig[this.unitType];

        activeProjectileCount++;
        const bolt = new GpuParticleEmitter(projectile?.particle({ pos: this.pos }));
        engine.add(bolt);
        bolt.z = 2000;

        const offsetPosition = (this.scene as BaseLevel).getOffsetPosition();
        const boltTarget = vec(
          offsetPosition.x + Math.random() * 70 - 35,
          offsetPosition.y - 50 + Math.random() * 50 - 25,
        );

        const speed = projectile?.speed || 200;
        bolt.actions
          .moveTo(boltTarget, speed)
          .callMethod(() => {
            activeProjectileCount--;
          })
          .die();
      };

      this.boltInterval = setInterval(fireProjectile, baseDelay);
    }

    this.positionSelf(engine);

    const shadow = new Actor({
      pos: vec(0, 0),
      collisionType: CollisionType.PreventCollision,
      anchor: vec(0.5, -0.9),
    });
    shadow.scale = vec(1.2, 1.2);
    shadow.graphics.use(Images.unit_shadow.toSprite());
    shadow.graphics.opacity = 0.5;
    shadow.z = -100;
    this.addChild(shadow);

    // Only show smoke effect for first 20 units of each type to reduce actor count
    if (this.countOfType <= 20) {
      const smokeEffect = new Actor({
        pos: vec(0, 0),
        anchor: vec(0.5, 0.3),
        collisionType: CollisionType.PreventCollision,
      });
      smokeEffect.scale = vec(0.5, 0.3);
      smokeEffect.graphics.use(smokeAnimation.clone());
      this.addChild(smokeEffect);
    }
  }

  private positionSelf(engine: Engine) {
    const placedOnLeft = Math.random() > 0.5;

    const posRules = positioningRules[this.unitType];
    const sign = placedOnLeft ? -1 : 1;

    const xSpread = Math.min(posRules.spread, (posRules.spread * this.countOfType) / 50);

    let xPos = sign * randomGaussianValue(posRules.center, xSpread, posRules.skew);

    let yPos = randomGaussianValue(0, 50 + Math.abs(xPos), 0);

    xPos = adjustToPerspective(xPos, yPos);
    xPos = adjustAroundCenter(xPos, yPos);

    // Check if too close to the center. If so, move it down.
    if (isInsideElipsis(xPos, yPos, 200, 120)) {
      xPos *= 1.1;
      yPos -= Math.random() * 100 - 50;
    }

    const { x: targetX, y: targetY } = (this.scene as BaseLevel).getAttackTargetPosition();

    // TODO: Remove when we have more than 1 catapult
    if (this.unitType === 'catapult') {
      yPos *= 0.3;
    }

    this.pos = vec(xPos + targetX, yPos + targetY);

    this.z = this.pos.y + 30;

    if (this.pos.x > targetX) {
      this.graphics.flipHorizontal = true;
    }
  }
}
